<template>
  <div class="d-flex flex-column gap-4">
    <!-- Per Page Component -->
    <PerPage
      v-if="pagination"
      :pagination="pagination"
      :showing-entries="true"
      @on-page-change="handlePerPageChange"
    />

    <!-- Data Table -->
    <DataTable
      id="retained-datatable"
      class="datatable bg-white text-center mt-3"
      :fields="activeTableFields"
      :items="retainedAppointments"
      :no-items-view="{
        noResults: 'No items available',
        noItems: 'No items available'
      }"
      :sorter="{ external: true }"
      :column-filter="isTabular?.value"
      @update:sorter-value="handleSortByColumn"
    >
      <template #user="{ item }">
        <td>
          <UserAptPass
            :appointments="true"
            :pass="item"
            :from-retained="true"
          />
        </td>
      </template>
      <template #user-filter>
        <input
          :value="columnSearchKeys.user.value"
          placeholder="Search by name"
          class="form-control form-control-sm my-2"
          @input="handleColumnSearch($event.target.value, 'user')"
        />
      </template>

      <template #recurrence_appointment_pass="{ item }">
        <td>
          <div class="appointment-date-info">
            <p class="mb-1">
              <b>{{ $helpers.transformDate(item?.for_date, "MMM D YYYY") }}</b>
            </p>
            <p v-if="item.recurrence_type" class="mb-1">
              <span class="text-muted">{{ item?.recurrence_type }}</span>
            </p>
          </div>
        </td>
      </template>
      <template #recurrence_appointment_pass-filter>
        <input
          :value="columnSearchKeys.recurrence_appointment_pass.value"
          placeholder="Search by date"
          class="form-control form-control-sm my-2"
          @input="
            handleColumnSearch(
              $event.target.value,
              'recurrence_appointment_pass'
            )
          "
        />
      </template>

      <template #period="{ item }">
        <td>
          <div>
            <b v-if="item?.period">{{ item?.period?.name || "-" }}</b>
            <div class="v3base-gray">
              {{
                item?.for_date
                  ? $helpers.transformDate(item?.for_date, "h:mm A")
                  : ""
              }}
            </div>
          </div>
        </td>
      </template>
      <template #period-filter>
        <input
          :value="columnSearchKeys.period.value"
          placeholder="Search by period"
          class="form-control form-control-sm my-2"
          @input="handleColumnSearch($event.target.value, 'period')"
        />
      </template>

      <template #from="{ item }">
        <td>{{ item?.from?.name || "-" }}</td>
      </template>
      <template #from-filter>
        <input
          :value="columnSearchKeys.from.value"
          placeholder="Search by from"
          class="form-control form-control-sm my-2"
          @input="handleColumnSearch($event.target.value, 'from')"
        />
      </template>

      <template #to="{ item }">
        <td class="pass-time-info">
          <b v-if="item?.to && item?.to === 'App\\Models\\User'">
            {{
              item?.to ? item?.to?.first_name + " " + item?.to?.last_name : "-"
            }}
          </b>
          <b v-else>{{ item?.to ? item?.to?.name : "-" }}</b>
          <b
            v-if="
              item?.acknowledged_by_user &&
              item?.to &&
              item?.acknowledged_by_user.id === item?.to?.id
            "
            ><span class="checked-icon ms-2"
          /></b>
        </td>
      </template>
      <template #to-filter>
        <input
          :value="columnSearchKeys.to.value"
          placeholder="Search by to"
          class="form-control form-control-sm my-2"
          @input="handleColumnSearch($event.target.value, 'to')"
        />
      </template>

      <template #created_by_user="{ item }">
        <td>
          <div v-if="item?.created_by_user">
            <b>{{
              item?.created_by_user?.first_name +
              " " +
              item?.created_by_user?.last_name
            }}</b>
            <p v-if="item?.created_at">
              {{ $helpers.transformDate(item?.created_at, "MM/D/YY") }}
              {{ $helpers.transformDate(item?.created_at, "hh:mm A") }}
            </p>
          </div>
        </td>
      </template>
      <template #created_by_user-filter>
        <input
          :value="columnSearchKeys.created_by_user.value"
          placeholder="Search by creator"
          class="form-control form-control-sm my-2"
          @input="handleColumnSearch($event.target.value, 'created_by_user')"
        />
      </template>

      <template #comments="{ item }">
        <td>
          <PassComments
            :pass="item"
            :from-retained="true"
            commentable-type="App\Models\AppointmentPassRetained"
            style="cursor: pointer"
          />
        </td>
      </template>
      <template #comments-filter>
        <input
          :value="columnSearchKeys.comments.value"
          placeholder="Search by comments"
          class="form-control form-control-sm my-2"
          @input="handleColumnSearch($event.target.value, 'comments')"
        />
      </template>
    </DataTable>

    <!-- Pagination -->
    <Pagination
      v-if="pagination && pagination.pages > 1 && !isLazyLoadingMode"
      class="cs-pagination mt-4"
      :dots="false"
      :double-arrows="true"
      :active-page="pagination.activePage"
      :pages="pagination.pages"
      align="center"
      size="lg"
      @update:active-page="handlePageChange"
    />
  </div>
</template>

<script>
import UserAptPass from "@/v3components/Datatables/AdultAptPassDataTable/UserAptPass.vue"
import DataTable from "@/v3components/shared/DataTable/DataTable.vue"
import Pagination from "@/v3components/shared/DataTable/Pagination.vue"
import PerPage from "@/v3components/shared/DataTable/Perpage.vue"
import PassComments from "@/v3components/shared/PassComponents/PassComments.vue"
import { reactive } from "vue"

export default {
  name: "RetainedTableCore",
  components: {
    DataTable,
    Pagination,
    PerPage,
    PassComments,
    UserAptPass
  },
  props: {
    activeTableFields: {
      type: Array,
      default: () => []
    },
    retainedAppointments: {
      type: Array,
      default: () => []
    },
    pagination: {
      type: Object,
      default: null
    },
    columnSearchKeys: {
      type: Object,
      default: () => ({})
    },
    isTabular: {
      type: Object,
      default: null
    },
    isLazyLoadingMode: {
      type: Boolean,
      default: false
    }
  },
  emits: ["column-sort", "page-changed", "per-page-changed", "column-search"],
  setup(props, { emit }) {
    const state = reactive({
      filter: false
    })

    const handleSortByColumn = (column) => {
      emit("column-sort", column)
    }

    const handlePerPageChange = (option) => {
      emit("per-page-changed", option)
    }

    const handlePageChange = (page) => {
      emit("page-changed", page)
    }

    const handleColumnSearch = (value, columnKey) => {
      emit("column-search", { value, columnKey })
    }

    return {
      state,
      handleSortByColumn,
      handlePerPageChange,
      handlePageChange,
      handleColumnSearch
    }
  }
}
</script>
